/**
 * **The graphics the product ships** — C24 item 3, ADR-0047 §1, T-4.123.
 *
 * The files are SVGs written in this repo, under `apps/web/site-assets/stock/`,
 * and `manifest.json` beside them is the one list: file → group → the words a
 * firm reads beside it → the box it reserves. `scripts/copy-stock.mjs` copies
 * the list into `dist/site-assets/stock/` at build time and **fails the build
 * on an entry with no file, a script inside an SVG, an off-origin reference or
 * a dimension that disagrees with the drawing's own `viewBox`** — so a card
 * this picker offers is a picture the server has, and the box it reserves is
 * the shape that arrives. They are served first-party at
 * `/site-assets/stock/<file>` on every host, the way the fonts and the default
 * logos are, which is what lets a firm use one with no upload and no
 * third-party request from its website.
 *
 * **They are drawings, not photographs**, and the picker says so in as many
 * words (*Graphics we ship*). A firm looking for a photograph of its own
 * office is not left to discover that these are not it. Why drawings: a
 * photograph in a repo is a licence to carry and a credit to render, these
 * ship today where photographs would wait on files, and a drawing works at
 * 390px and at 2560px where a photograph compromises. Spec §9-81 is where
 * photographs are asked for, and the manifest accepts a raster entry
 * unchanged.
 *
 * **Nothing here is in any firm's library.** A shipped graphic cannot be
 * deleted, is not counted against the storage cap, and *where it is used* says
 * nothing about it. That asymmetry is deliberate (ADR-0047 *Consequences*) and
 * the picker does not pretend otherwise.
 */

import manifest from '../../site-assets/stock/manifest.json'

export const STOCK_PREFIX = '/site-assets/stock/'

export interface StockGroup {
  key: string
  label: string
}

export interface StockPicture {
  /** The stored value: `/site-assets/stock/<file>`. */
  url: string
  file: string
  group: string
  /** The group's words, carried so a card needs no second lookup. */
  groupLabel: string
  /** What the firm reads beside the card, and the default `alt`. */
  label: string
  /** The drawing's own box — held to its `viewBox` by the build. */
  width: number
  height: number
}

export const STOCK_GROUPS: StockGroup[] = manifest.groups.map((group) => ({
  key: group.key,
  label: group.label,
}))

export const STOCK_PICTURES: StockPicture[] = manifest.pictures.map((entry) => ({
  url: `${STOCK_PREFIX}${entry.file}`,
  file: entry.file,
  group: entry.group,
  groupLabel: entry.group_label,
  label: entry.label,
  width: entry.width,
  height: entry.height,
}))

/** The shipped graphic a stored value names, or `null` for anything else. */
export function stockPictureOf(url: string | null | undefined): StockPicture | null {
  if (!url) return null
  return STOCK_PICTURES.find((picture) => picture.url === url) ?? null
}

/** Whether a stored value is one of ours. */
export function isStockPicture(url: string | null | undefined): boolean {
  return stockPictureOf(url) !== null
}

/**
 * The gallery, grouped, in the manifest's order — and **only the groups that
 * have a picture**, so a group added to the manifest ahead of its drawings
 * does not show as an empty heading.
 */
export function stockByGroup(): { group: StockGroup; pictures: StockPicture[] }[] {
  return STOCK_GROUPS.map((group) => ({
    group,
    pictures: STOCK_PICTURES.filter((picture) => picture.group === group.key),
  })).filter((section) => section.pictures.length > 0)
}

/**
 * The graphics a search matches — over the label and the group's words, which
 * is everything a firm can actually read on a card. Case and surrounding
 * space are ignored; an empty query is every picture.
 */
export function searchStock(query: string): StockPicture[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return STOCK_PICTURES
  return STOCK_PICTURES.filter(
    (picture) =>
      picture.label.toLowerCase().includes(needle) ||
      picture.groupLabel.toLowerCase().includes(needle) ||
      picture.file.toLowerCase().includes(needle),
  )
}
